// The inline editor of a feed, in the panel: the name and the sources chosen
// among the favourites, «Salva» / «Annulla». Used for a new feed and for a
// change. Built from Platea's own elements: the field is the «Cerca la
// fonte» field of the sources panel (src/components/sidebar/panels.tsx), the
// rows with a tick are the sources list of that panel, the buttons are the
// small white ones of a status row (src/components/PanelStatus.tsx).
import { useMemo, useState } from "react";
import { Button, Input, Label, TextField } from "@heroui/react";

import type { Source, SubSource } from "../../data/mock";
import { PanelStatus, STATUS_BUTTON_CLASS } from "../../components/PanelStatus";
import { ScrollArea } from "../../components/sidebar/panels";
import { SourceList } from "../../components/sidebar/SourceList";
import { toSelection } from "../news/adapt";
import { NAME_IN_USE, NAME_RESERVED, toggleKeys, type NameProblem } from "./adapt";

const NO_FAVOURITES = "Nessun preferito: scegli le fonti in Ultime news.";
const NAME_PROBLEM: Record<NameProblem, string> = { "in-use": NAME_IN_USE, reserved: NAME_RESERVED };

export type FeedEditorError = { message: string; onRetry?: () => void };

type FeedEditorProps = {
  initialName: string;
  initialSources: string[];
  /** The favourites to choose from: the sources tree cut down to them. */
  favourites: Source[];
  /** Why a name cannot be the feed's (reserved, another feed's already: the api would refuse it), if so. */
  nameProblem: (name: string) => NameProblem | null;
  /** The label of the button that sends: «Crea» for a new feed, «Salva» for a change. */
  saveLabel: string;
  /** The call is on its way: «Salva» waits, nothing can be changed. */
  pending: boolean;
  error: FeedEditorError | null;
  onSave: (name: string, sources: string[]) => void;
  onCancel: () => void;
};

export function FeedEditor({
  initialName,
  initialSources,
  favourites,
  nameProblem,
  saveLabel,
  pending,
  error,
  onSave,
  onCancel,
}: FeedEditorProps) {
  const [name, setName] = useState(initialName);
  const [sources, setSources] = useState<string[]>(() => [...initialSources].sort());
  const selection = useMemo(() => toSelection({ active: favourites }, sources), [favourites, sources]);
  // Parlamento open at the start, as in the sources panel
  const defaultExpanded = useMemo(
    () => favourites.filter((s) => s.name.trim().toLowerCase() === "parlamento").map((s) => s.id),
    [favourites],
  );

  const trimmed = name.trim();
  const problem = trimmed === "" ? null : nameProblem(trimmed);
  const canSave = trimmed !== "" && problem === null && !pending;

  // while the call is on its way the ticks keep showing the choice sent, and a click changes nothing
  const onSelect = (item: Source | SubSource, checked: boolean) => {
    if (pending) return;
    setSources((s) => toggleKeys(s, item, checked));
  };

  return (
    <div className="flex flex-col gap-2.5">
      <TextField value={name} onChange={setName} isDisabled={pending} className="flex w-full flex-col gap-1">
        <Label className="text-sm leading-6 font-medium text-accent">Nome del feed</Label>
        <Input
          placeholder="Nome del feed"
          className="h-9 w-full rounded-xl border-0 bg-field-background px-3 text-sm leading-5 shadow-field"
        />
      </TextField>
      {problem && <PanelStatus role="note">{NAME_PROBLEM[problem]}</PanelStatus>}

      <span className="text-sm leading-6 font-medium text-accent">Fonti</span>
      {favourites.length === 0 ? (
        <PanelStatus>{NO_FAVOURITES}</PanelStatus>
      ) : (
        <ScrollArea>
          <SourceList
            sources={favourites}
            selection={selection}
            onSelect={onSelect}
            defaultExpandedKeys={defaultExpanded}
          />
        </ScrollArea>
      )}

      {error && (
        <PanelStatus role="alert" onRetry={error.onRetry}>
          {error.message}
        </PanelStatus>
      )}

      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          size="sm"
          isDisabled={!canSave}
          onPress={() => onSave(trimmed, sources)}
          className={STATUS_BUTTON_CLASS}
        >
          {saveLabel}
        </Button>
        <Button size="sm" isDisabled={pending} onPress={onCancel} className={STATUS_BUTTON_CLASS}>
          Annulla
        </Button>
      </div>
    </div>
  );
}
